import type { RouteLocationRaw } from 'vue-router';
import { AREAS, areaForEntity, listPathFor, pageForRoute, type Area, type AreaId } from '@/navigation/areas';

/** The part of a route the area lookup needs, so tests can pass a plain object. */
export interface RouteLike {
    name?: string | symbol | null;
    path: string;
    meta: Record<string, unknown>;
}

/**
 * Which area a route belongs to. An entity's list or record belongs to that
 * entity's area. A page belongs to the area that lists it, for example
 * Permissions to Settings. Anything else, including Home, is Home.
 */
export function areaIdForRoute(route: RouteLike): AreaId {
    if (typeof route.meta.entityId === 'string') {
        return areaForEntity(route.meta.entityId)?.id ?? 'home';
    }
    const routeName = typeof route.name === 'string' ? route.name : '';
    for (const area of AREAS) {
        if (area.pages.some((page) => page.routeName === routeName || route.path.startsWith(`${page.path}/`))) {
            return area.id;
        }
    }
    return 'home';
}

/** True on an entity's list page, and false on a record page or any other page. */
export function isEntityListRoute(route: { meta: Record<string, unknown>; params?: Record<string, unknown> }): boolean {
    return typeof route.meta.entityId === 'string' && route.params?.id === undefined;
}

/**
 * True where there is a table to save as a view: an entity's list, or a page
 * with saved views of its own, such as Track matching. Creating a view and
 * browsing the team's shared views are offered only here.
 */
export function routeCanSaveView(route: RouteLike & { params?: Record<string, unknown> }): boolean {
    if (isEntityListRoute(route)) {
        return true;
    }
    const routeName = typeof route.name === 'string' ? route.name : '';
    return pageForRoute(routeName, route.path)?.savedViewEntityId !== undefined;
}

/** Where clicking an area's icon goes: its first list, else its first page, else Home. */
export function areaLanding(area: Area): RouteLocationRaw {
    if (area.entities.length > 0) {
        return listPathFor(area.entities[0].entityId);
    }
    if (area.pages.length > 0) {
        return area.pages[0].path;
    }
    return '/';
}
